import type { Provider } from "./assessment.ts";
import type { IdentityType, MfaStatus } from "./assessment/data/index.ts";
import type { Shutdownable } from "./index.ts";

/** The directory integrations P0 ships. Deliberately a closed set: the keys
 * themselves import nothing, and assessment rendering keys off them.
 */
export const DirectoryKeys = [
  "azure-ad",
  "entra-id",
  "okta",
  "workspace",
] as const;
export type DirectoryKey = (typeof DirectoryKeys)[number];
export const isDirectoryKey = (key: string): key is DirectoryKey =>
  DirectoryKeys.includes(key as any);

export type Group = {
  id: string;
  label?: string;
};

export type Role = {
  id: string;
  label?: string;
};

export const GroupTypes = ["group", "federated"] as const;
export type GroupType = (typeof GroupTypes)[number];

export type DirectoryUser = {
  type: "user";
  department?: string;
  directory: string;
  external?: boolean;
  id: string;
  label: string;
  email?: string;
  lastLogin: string | undefined;
  manager?: string;
  mfa: MfaStatus;
  aliases: string[];
  profile?: {
    [key: string]: unknown;
  } & {
    firstName?: string;
    lastName?: string;
    displayName?: string;
  };
};

export type GroupPrivilege =
  /** Can add group members */
  | "add"
  /** Can approve group join requests */
  | "approve"
  /** Can directly join this group */
  | "join"
  /** Can view group data content (e.g., messages if this is a mailing group) */
  | "view";

// Missing indicates no access
export type GroupAccessIdentity =
  /** Action can only be performed by group admins */
  | "admin"
  /** Action can be performed by anyone in the organization domain */
  | "domain"
  /** Action can be performed by any member of the group */
  | "group"
  /** Action can only be performed by an invited user */
  | "invited"
  /** Action can only be performed by the resource owner */
  | "owner"
  /** Action can be performed by anyone */
  | "public"
  /** Access principal is unknown */
  | "unknown";

export type DirectoryGroup = Group & {
  access?: Partial<Record<GroupPrivilege, GroupAccessIdentity>>;
  /** The name of the directory in which this group resides */
  directory: string;
  /** Human-friendly name for this group */
  label: string;
  /** Group members */
  members: DirectoryMember[];
  // `type` is of unknown significance?
  type: GroupType;
};

export type DirectoryRole = Role & {
  /** The name of the directory in which this role resides */
  directory: string;
  /** Human-friendly name for this role */
  label: string;
  /** Role description */
  description?: string;
  /** Whether this role is managed by the provider */
  isProviderManaged?: boolean;
  /** Whether this role is enabled */
  isEnabled?: boolean;
};

export type DirectoryServiceAccount = {
  id: string;
  label: string;
  type: "service-account";
  lastLogin?: string;
  members?: DirectoryMember[];
};

export type ExternalIdentity = {
  id: string;
  label: string;
  provider: Provider;
  type: IdentityType;
  parent?: string;
  external: true;
};

export type DirectoryIdentity =
  | DirectoryGroup
  | DirectoryServiceAccount
  | DirectoryUser
  | ExternalIdentity;

export type DirectoryMember = Pick<
  DirectoryIdentity,
  "id" | "label" | "type"
> & { provider?: Provider; parent?: string };

export const isDirectoryUser = (
  identity: DirectoryIdentity
): identity is DirectoryUser => identity.type === "user";

export const isExternalIdentity = (
  identity: DirectoryIdentity
): identity is ExternalIdentity =>
  "external" in identity && !!identity.external;

export const DEFAULT_MANAGER_EMAIL_FIELD = "manager";

/** Methods for reading and mutating a single user */
export interface UserDirectory {
  /** Adds a user to a group */
  addToGroup(email: string, groupId: string): Promise<void>;

  /** Adds a user to a role */
  addToRole?(email: string, roleId: string): Promise<void>;

  /** Gets a user by email address */
  get(email: string): Promise<DirectoryUser>;

  /** Gets a user's groups
   *
   * Includes transitively joined groups.
   */
  groups(email: string): AsyncGenerator<DirectoryGroup>;

  /** Gets a user's roles */
  roles?(email: string): AsyncGenerator<DirectoryRole>;

  /** List all users */
  /*
   * The partialEmail parameter is used to search for users by their email address.
   * Some directory integrations may support searching by first name or last name as well, but
   * the primary use case is to filter users by their email address.
   */
  list(params?: UserDirectoryListParams): AsyncGenerator<DirectoryUser>;

  /**
   * Get a user's manager's email as defined in the directory.
   */
  managerEmail(email: string, field?: string): Promise<string | undefined>;

  /** Removes a user from a group */
  removeFromGroup(email: string, groupId: string): Promise<void>;

  /** Removes a user from a role */
  removeFromRole?(email: string, roleId: string): Promise<void>;
}

export type UserDirectoryListParams = {
  partialEmail?: string;
  /** If true, return only user with id and email
   *  Added because listing all users with signin activity is only supported with Entra premium
   *  and requires extra permissions
   */
  emailOnly?: true;
  requireMfaData?: boolean;
  /**
   * Google Workspace parameter,
   * see https://developers.google.com/workspace/admin/directory/reference/rest/v1/users/list#viewtype
   *
   * TODO: ENG-5427: Remove workspaceViewType field.
   * This is currently a workaround to ensure that this works with the user lister
   * for the p0 allow modal for tenants (both local dev & prod) if the user who installs
   * the Workspace integration is not a Workspace admin. This probably doesn't actually make long-term sense
   * to have. Instead, what probably makes the most sense is to prevent non-Workspace admins from
   * installing the Workspace integration. If that were the case, we could remove
   * this parameter and always use the admin_view default (though that means the p0 allow modal user lister
   * for Workspace wouldn't work unless a Workspace admin installed the integration).
   */
  workspaceViewType?: "admin_view" | "domain_public";
};

export type GroupDirectoryListOptions = {
  /** If true, only return groups that may be granted by P0 */
  grantableOnly: boolean;
  /** If true, gather access settings for the groups */
  expandAccess: boolean;
  /** If true, return group members */
  expandMembers: boolean;
};

export type RoleDirectoryListOptions = {
  /** If true, only return roles that may be granted by P0 */
  builtInOnly: boolean;
};

export type GroupDirectoryMembersOptions = {
  includeTransitive: boolean;
  usersOnly: boolean;
};

export interface GroupDirectory {
  /** Get a group's details from its ID */
  get(groupId: string): Promise<DirectoryGroup>;

  /** List all groups */
  list(options: GroupDirectoryListOptions): AsyncGenerator<DirectoryGroup>;

  /** List all members of groups. Defaults to returning only direct members, unless includeTransitive is provided in options */
  members(
    groupId: string,
    options: GroupDirectoryMembersOptions
  ): AsyncGenerator<DirectoryIdentity>;
}

export interface RoleDirectory {
  /** Get a role's details from its ID */
  get(roleId: string): Promise<DirectoryRole>;

  /** List all roles */
  list(options: RoleDirectoryListOptions): AsyncGenerator<DirectoryRole>;
}

export interface Directory<
  K extends DirectoryKey = DirectoryKey,
> extends Shutdownable {
  key: K;
  group: GroupDirectory;
  roleAssignment?: RoleDirectory;
  user: UserDirectory;
}

export type DirectoryProcessor = {
  /** Process Users from directory integration specific api contract*/
  users(users: DirectoryUser[]): {
    users: DirectoryUser[];
    aliases: Record<string, string>;
  };
  /** Process group members from directory integration specific api contract */
  groupMembers(
    members: DirectoryIdentity[],
    aliases: Record<string, string>
  ): DirectoryIdentity[];
};
